import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface PeopleData {
  total_users: number;
  new_this_week: number;
  new_this_month: number;
  active_users_30d: number;
  by_tier: Record<string, number>;
  by_locale: Record<string, number>;
}

const TIER_LABELS: Record<string, string> = {
  seedling:    "Seedling",
  ember:       "Ember",
  keeper:      "Keeper",
  storyteller: "Storyteller",
};

// Display order for tiers (most relevant first)
const TIER_ORDER = ["seedling", "ember", "keeper", "storyteller"];

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function PeopleDashboard() {
  const [data, setData] = useState<PeopleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase.rpc(
      "get_people_dashboard"
    );
    if (rpcError) {
      setError(rpcError.message);
      setLoading(false);
      return;
    }
    setData(result as unknown as PeopleData);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function formatLastUpdated(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 minute ago";
    return `${diffMin} minutes ago`;
  }

  const pct = (n: number, total: number) =>
    total === 0 ? "0%" : `${Math.round((n / total) * 100)}%`;

  const sortedTiers = data
    ? TIER_ORDER.filter((t) => data.by_tier[t] !== undefined).concat(
        Object.keys(data.by_tier).filter((t) => !TIER_ORDER.includes(t))
      )
    : [];

  const sortedLocales = data
    ? Object.entries(data.by_locale).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">People</h1>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              Last updated: {formatLastUpdated(lastUpdated)}
            </p>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">Failed to load</p>
          <p className="text-xs text-red-500 mt-0.5">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 p-5 h-24 animate-pulse"
              >
                <div className="h-2 bg-gray-100 rounded w-16 mb-3" />
                <div className="h-8 bg-gray-100 rounded w-12" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data */}
      {data && (
        <div className="space-y-6">
          {/* Row 1 — headline numbers */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Total users" value={data.total_users} />
            <StatCard
              label="Active this month"
              value={data.active_users_30d}
              sub="memory in last 30 days"
            />
            <StatCard label="New this week" value={data.new_this_week} />
            <StatCard label="New this month" value={data.new_this_month} />
          </div>

          {/* Row 2 — subscription breakdown */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Subscription tiers
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {sortedTiers.map((tier) => {
                const count = data.by_tier[tier] ?? 0;
                return (
                  <div
                    key={tier}
                    className="bg-white rounded-lg border border-gray-200 p-5"
                  >
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {TIER_LABELS[tier] ?? tier}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {pct(count, data.total_users)} of users
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Row 3 — locale breakdown */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Language breakdown
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Users</th>
                    <th>% of total</th>
                    <th style={{ width: "40%" }}>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLocales.map(([lang, count]) => (
                    <tr key={lang}>
                      <td className="font-mono text-gray-700">{lang}</td>
                      <td className="text-gray-900 font-medium">{count}</td>
                      <td className="text-gray-500">
                        {pct(count, data.total_users)}
                      </td>
                      <td>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-orange-400 rounded-full"
                            style={{
                              width: pct(count, data.total_users),
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
